<script setup lang="ts">
import { ref, watch, useTemplateRef } from "vue";
import { z } from "zod";
import { UiFormBuilder, UiStandardButton, UiInfoBox } from "@shopexperts/shared";
import type { BuilderRowConfig, Redirect } from "@shopexperts/shared";

export interface RedirectFormProps {
  editing?: Redirect | null;
}

const props = withDefaults(defineProps<RedirectFormProps>(), {
  editing: null,
});

const emit = defineEmits<{
  (e: 'submit', value: { 
    source_path: string; 
    target_path: string; 
    status_code: number; 
    is_active: boolean;
    expires_at: string | null;
  }): void;
  (e: 'cancel'): void;
}>();

// Schema for form validation
const schema = z.object({
  source_path: z.string()
    .min(1, "Source path is required")
    .refine((val) => val.startsWith("/"), "Source path must start with /"),
  target_path: z.string()
    .min(1, "Target path is required")
    .max(2048, "Target path must be less than 2048 characters"),
  status_code: z.string(),
  is_active: z.boolean(),
  expires_at: z.string().optional(),
});

// Status code options (limited to common web page redirects)
const statusCodeOptions = [
  { value: '301', label: '301 - Permanent Redirect' },
  { value: '302', label: '302 - Temporary Redirect' },
];

// Form rows configuration
const rows: BuilderRowConfig[] = [
  {
    columns: [
      { 
        name: 'source_path', 
        type: 'text', 
        label: 'Source Path', 
        hint: 'The URL path to redirect from (must start with /)',
        props: { placeholder: '/old-page' } 
      },
    ],
  },
  {
    columns: [
      { 
        name: 'target_path', 
        type: 'text', 
        label: 'Target Path', 
        hint: 'The URL path or full URL to redirect to',
        props: { placeholder: '/new-page or https://example.com/page' } 
      },
    ],
  },
  {
    columns: [
      { 
        name: 'status_code', 
        type: 'select', 
        label: 'Status Code', 
        hint: 'Choose based on whether this is a permanent or temporary redirect',
        props: { 
          options: statusCodeOptions,
        } 
      },
    ],
  },
  {
    columns: [
      { 
        name: 'is_active', 
        type: 'checkbox', 
        props: {
          label: 'Active',
        },
        hint: 'Enable or disable this redirect',
      },
    ],
  },
  {
    columns: [
      { 
        name: 'expires_at', 
        type: 'text', 
        label: 'Expires At (Optional)', 
        hint: 'ISO date string (e.g., 2025-12-31T23:59:59Z). Leave empty for no expiration.',
        props: { placeholder: '2025-12-31T23:59:59Z' } 
      },
    ],
  },
];

// Form model
const model = ref<{ 
  source_path: string; 
  target_path: string; 
  status_code: string;
  is_active: boolean;
  expires_at: string;
}>({ 
  source_path: '', 
  target_path: '', 
  status_code: '301',
  is_active: true,
  expires_at: '',
});

// Watch for editing changes to populate form
watch(() => props.editing, (newEditing) => {
  if (newEditing) {
    model.value = {
      source_path: newEditing.source_path,
      target_path: newEditing.target_path,
      status_code: String(newEditing.status_code),
      is_active: newEditing.is_active,
      expires_at: newEditing.expires_at || '',
    };
  } else {
    model.value = { 
      source_path: '', 
      target_path: '', 
      status_code: '301',
      is_active: true,
      expires_at: '',
    };
  }
}, { immediate: true });

const formRef = useTemplateRef('formRef');

defineExpose({
  resetForm: () => {
    formRef.value?.resetForm();
    model.value = { 
      source_path: '', 
      target_path: '', 
      status_code: '301',
      is_active: true,
      expires_at: '',
    };
  },
});

function onSubmit(values: { 
  source_path: string; 
  target_path: string; 
  status_code: string;
  is_active: boolean;
  expires_at?: string;
}) {
  emit('submit', {
    source_path: values.source_path,
    target_path: values.target_path,
    status_code: parseInt(values.status_code, 10),
    is_active: values.is_active,
    expires_at: values.expires_at && values.expires_at.trim() !== '' 
      ? values.expires_at 
      : null,
  });
}

function onCancel() {
  emit('cancel');
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Status Code Explanation -->
    <UiInfoBox variant="info" title="Understanding Redirect Types">
      <ul class="space-y-1.5 mt-1">
        <li>
          <strong>301 - Permanent:</strong> 
          Use when a page has permanently moved. Search engines will update their index to the new URL.
        </li>
        <li>
          <strong>302 - Temporary:</strong> 
          Use when a page is temporarily unavailable or moved. The original URL will remain in search engine indexes.
        </li>
      </ul>
    </UiInfoBox>

    <UiFormBuilder 
      ref="formRef" 
      :schema="schema" 
      :rows="rows" 
      scroll-to-first-error 
      v-model="model"
      @submit="onSubmit"
    >
      <template #actions>
        <div class="flex items-center gap-3 pt-4">
          <UiStandardButton 
            type="submit" 
            variant="primary" 
            :label="props.editing ? 'Update Redirect' : 'Create Redirect'" 
          />
          <UiStandardButton 
            variant="ghost" 
            label="Cancel" 
            @click="onCancel"
          />
        </div>
      </template>
    </UiFormBuilder>
  </div>
</template>

